/**
 * What the host has on top of the guest's live room: the requests that cannot be missed (a share ask, a raised hand),
 * the host tools sheet, small rooms, questions, and the page after the live. The room itself is the guest's
 * (`LiveRoom`); these are placed in it, in the same look.
 */
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ChatLine, ShareGuest, ShareRule } from "../../../shared/live-protocol";
import { actionsFor, type MomentAction, type MomentRun, type MomentStanding, standingOf } from "../../../shared/live-moments";
import type { InteractionType } from "../../../shared/config";
import type { LiveStudio, LiveStudioMoment, WebinarLive } from "../../../shared/types";
import { api } from "../../lib/api";
import { type Meeting, type MeetingFace, type MeetingState } from "../../live/meeting/meeting";
import { useQuestions } from "../../live/questions";
import type { RoomChat } from "../../live/room/chat";
import { cameLine, replayGoes, replayLine } from "../../live/studioWords";
import { useMediaQuery, PHONE_QUERY } from "../smallScreen";
import { Sheet } from "../room/chrome";
import { GIcon } from "../room/GIcon";
import { circlePath, everyonePath, roomHome } from "../../talk/places";
import { teamTestsLine } from "../../views/rooms/liveWords";
import { firstOf } from "./liveParts";

/** Everything the host's page hands the room beyond what a guest's page does. */
export interface HostKit {
  onEnd: () => void;
  /** Where Back goes; while live the studio asks first. */
  backTo: string;
  shareUrl: string | null;
  onCopy: () => void;
  onPauseRecording: (paused: boolean) => void;
  recordingSec: string;
  /** How many the room counts as here. */
  here: string;
  /** The room's mood line, "the room is laughing". */
  mood: string;
  onRule: (rule: ShareRule) => void;
  onAnswer: (registrantId: string, yes: boolean) => void;
  lineTools: { pin: (id: string | null) => void; hide: (id: string) => void; reply: (line: ChatLine) => void };
  moments: ReactNode | null;
  chime: { on: boolean; set: (on: boolean) => void };
}

/** From this many hands up, they are one quiet count and not a card each. */
export const MANY_HANDS = 10;
/** The most cards on screen at once. */
const MAX_CARDS = 3;

// --- the chime ---

/** One soft two-note chime made with the Web Audio API, so there is no file to load. */
export function useChime(on: boolean): () => void {
  const ctx = useRef<AudioContext | null>(null);
  return useCallback(() => {
    if (!on) return;
    try {
      const audio = (ctx.current ??= new AudioContext());
      void audio.resume();
      const t0 = audio.currentTime;
      [659.25, 880].forEach((freq, i) => {
        const osc = audio.createOscillator();
        const gain = audio.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const at = t0 + i * 0.14;
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(0.07, at + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.42);
        osc.connect(gain).connect(audio.destination);
        osc.start(at);
        osc.stop(at + 0.45);
      });
    } catch {
      // A browser that will not play a sound before a press just stays quiet.
    }
  }, [on]);
}

// --- who is asking ---

export interface Ask {
  key: string;
  kind: "share" | "hand";
  /** The registrant for a share ask, the face for a hand. */
  id: string;
  name: string;
  seq: number;
}

/** Every share ask and hand, oldest first, with the hands the host put off left out. */
export function useAsks(
  shareAsks: readonly ShareGuest[],
  hands: readonly MeetingFace[],
): { all: Ask[]; cards: Ask[]; extra: number; many: boolean; later: (key: string) => void } {
  const seqs = useRef(new Map<string, number>());
  const [put, setPut] = useState<ReadonlySet<string>>(new Set());
  const handKey = (f: MeetingFace) => `hand:${f.id}:${f.handAt}`;
  const items: Ask[] = [
    ...shareAsks.map((g) => ({ key: `share:${g.registrantId}`, kind: "share" as const, id: g.registrantId, name: g.name })),
    ...hands.map((f) => ({ key: handKey(f), kind: "hand" as const, id: f.id, name: f.name })),
  ].map((a) => {
    if (!seqs.current.has(a.key)) seqs.current.set(a.key, seqs.current.size);
    return { ...a, seq: seqs.current.get(a.key) ?? 0 };
  });
  const many = hands.length >= MANY_HANDS;
  const all = items.toSorted((a, b) => a.seq - b.seq);
  const wanted = all.filter((a) => (a.kind === "share" ? true : !many && !put.has(a.key)));
  const later = useCallback((key: string) => setPut((was) => new Set(was).add(key)), []);
  return { all, cards: wanted.slice(0, MAX_CARDS), extra: Math.max(0, wanted.length - MAX_CARDS), many, later };
}

/** Sets the browser tab's title to say someone is asking, so a host on another window sees it too. */
function useTabTitle(asking: Ask[]): void {
  const first = asking[0];
  const base = useRef(document.title);
  useEffect(() => {
    if (!first) return;
    const was = document.title;
    base.current = was;
    document.title = `(${asking.length}) ${firstOf(first.name)} ${first.kind === "share" ? "asks to share" : "raised a hand"}`;
    return () => {
      document.title = was;
    };
  }, [first, asking.length]);
}

/** The cards at the top right of the stage (above the dock on a phone), until the host answers each. */
export function HostAsks({
  asks,
  onAnswer,
  onCall,
  onSeeAll,
  chime,
}: {
  chime: () => void;
  asks: ReturnType<typeof useAsks>;
  onAnswer: (registrantId: string, yes: boolean) => void;
  onCall: (faceId: string) => void;
  onSeeAll: () => void;
}): ReactNode {
  useTabTitle(asks.all);
  const heard = useRef<Set<string> | null>(null);
  const keys = asks.all.filter((a) => a.kind === "share" || !asks.many).map((a) => a.key);
  const keyList = keys.join("|");
  // Why not on the first look: what was already waiting when the page opened is not new.
  useEffect(() => {
    const was = heard.current;
    heard.current = new Set(keys);
    if (was && keys.some((k) => !was.has(k))) chime();
  }, [keyList]);
  const { cards, extra, many } = asks;
  const handCount = asks.all.filter((a) => a.kind === "hand").length;
  if (cards.length === 0 && !many) return null;
  return (
    <div className="hk-asks" role="region" aria-label="Requests" aria-live="polite">
      {cards.map((a) =>
        a.kind === "share" ? (
          <div key={a.key} className="hk-card ask-share" role="group" aria-label={`${firstOf(a.name)} wants to share their screen`}>
            <span className="hk-top">
              <span className="hk-ic">
                <GIcon name="share" size={18} />
              </span>
              <b><bdi>{firstOf(a.name)}</bdi> wants to share</b>
            </span>
            <span className="hk-act">
              <button type="button" className="hk-go" onClick={() => onAnswer(a.id, true)}>
                Allow
              </button>
              <button type="button" className="hk-no" onClick={() => onAnswer(a.id, false)}>
                Not now
              </button>
            </span>
          </div>
        ) : (
          <div key={a.key} className="hk-card ask-hand" role="group" aria-label={`${firstOf(a.name)} raised a hand`}>
            <span className="hk-top">
              <span className="hk-ic">
                <GIcon name="hand" size={18} />
              </span>
              <b><bdi>{firstOf(a.name)}</bdi> raised a hand</b>
            </span>
            <span className="hk-act">
              <button type="button" className="hk-go" onClick={() => onCall(a.id)}>
                Call on
              </button>
              <button type="button" className="hk-no" onClick={() => asks.later(a.key)}>
                Later
              </button>
            </span>
          </div>
        ),
      )}
      {extra > 0 ? (
        <button type="button" className="hk-more" onClick={onSeeAll}>
          +{extra} more waiting, see them all
        </button>
      ) : null}
      {many ? (
        <button type="button" className="hk-count" onClick={onSeeAll}>
          <GIcon name="hand" size={16} />
          {handCount} hands up
        </button>
      ) : null}
    </div>
  );
}

// --- the Hands tab's top: who may share, and who is asking ---

const RULES: [ShareRule, string][] = [
  ["ask", "Ask first"],
  ["host", "Only you"],
  ["anyone", "Anyone"],
];

export function ShareRules({ rule, onRule }: { rule: ShareRule; onRule: (rule: ShareRule) => void }): ReactNode {
  return (
    <div className="hk-rules" role="radiogroup" aria-label="Who may share a screen">
      {RULES.map(([value, words]) => (
        <button key={value} type="button" role="radio" aria-checked={rule === value} className={rule === value ? "on" : undefined} onClick={() => onRule(value)}>
          {words}
        </button>
      ))}
    </div>
  );
}

// --- the host tools sheet ---

function Switch({ words, on, onFlip }: { words: string; on: boolean; onFlip: () => void }): ReactNode {
  return (
    <label className="tg">
      <input type="checkbox" checked={on} onChange={onFlip} />
      {words}
    </label>
  );
}

export function HostTools({
  kit,
  meeting,
  state,
  rule,
  hasHands,
  onRooms,
  onClose,
}: {
  kit: HostKit;
  meeting: Meeting;
  state: MeetingState;
  rule: ShareRule;
  hasHands: boolean;
  onRooms: () => void;
  onClose: () => void;
}): ReactNode {
  const rec = state.recording;
  return (
    <Sheet label="Host tools" onClose={onClose}>
      <div className="sx-in">
        <h3 className="sx-h">Host tools</h3>
        <div className="sx-sec">
          <p className="sx-l">Who may share a screen</p>
          <ShareRules rule={rule} onRule={kit.onRule} />
          <Switch words="Soft chime when someone asks" on={kit.chime.on} onFlip={() => kit.chime.set(!kit.chime.on)} />
        </div>
        <div className="sx-sec">
          <p className="sx-l">The room now</p>
          <p className="hk-line">
            <b>In the room</b>
            <span>{kit.here}</span>
          </p>
          <p className="hk-line">
            <b>How it feels</b>
            <span>{kit.mood || "Listening quietly"}</span>
          </p>
        </div>
        <div className="sx-sec">
          <p className="sx-l">The room</p>
          <button className="sx-row" type="button" onClick={() => void meeting.muteEveryone()}>
            <GIcon name="micoff" size={20} /> Mute everyone
          </button>
          {hasHands ? (
            <button className="sx-row" type="button" onClick={() => void meeting.lowerAllHands()}>
              <GIcon name="hand" size={20} /> Lower all hands
            </button>
          ) : null}
          <button
            className="sx-row"
            type="button"
            onClick={() => {
              onClose();
              onRooms();
            }}
          >
            <GIcon name="people" size={20} /> {state.rooms.length ? "Small rooms" : "Open small rooms"}
          </button>
        </div>
        <div className="sx-sec">
          <p className="sx-l">Recording</p>
          {rec === "off" ? (
            <p className="hk-note">Recording is off for this live.</p>
          ) : (
            <button className="sx-row" type="button" onClick={() => kit.onPauseRecording(rec === "on")}>
              <GIcon name={rec === "on" ? "pause" : "play"} size={20} /> {rec === "on" ? "Pause recording" : "Resume recording"}
              <span>{rec === "on" ? `Recording, ${kit.recordingSec}` : "Recording is paused"}</span>
            </button>
          )}
        </div>
        {kit.shareUrl ? (
          <div className="sx-sec">
            <p className="sx-l">Room link</p>
            <button className="sx-row" type="button" onClick={kit.onCopy}>
              <GIcon name="copy" size={20} /> Copy the room link
              <span>{kit.shareUrl.replace(/^https?:\/\//, "")}</span>
            </button>
          </div>
        ) : null}
        <button className="btn btn-g btn-w" type="button" style={{ marginTop: 6 }} onClick={onClose}>
          Done
        </button>
      </div>
    </Sheet>
  );
}

// --- small rooms ---

const ROOM_CHOICES = [2, 3, 4, 5, 6];

export function HostSmallRooms({ meeting, state, onClose }: { meeting: Meeting; state: MeetingState; onClose: () => void }): ReactNode {
  const [count, setCount] = useState(2);
  const [spread, setSpread] = useState<"auto" | "by-hand">("auto");
  const [busy, setBusy] = useState(false);
  const open = state.rooms.length > 0;
  const going = state.faces.filter((f) => !f.host);
  return (
    <Sheet label="Small rooms" onClose={onClose}>
      <div className="sx-in">
        <h3 className="sx-h">Small rooms</h3>
        {open ? (
          <>
            <div className="sx-sec">
              <p className="sx-l">Open now</p>
              {state.rooms.map((room) => (
                <p key={room.id} className="hk-line">
                  <b dir="auto">{room.title}</b>
                  <span>{room.here === 1 ? "1 person" : `${room.here} people`}</span>
                </p>
              ))}
            </div>
            {spread === "by-hand" ? (
              <div className="sx-sec">
                <p className="sx-l">Put people in</p>
                {going.slice(0, 12).map((face) => (
                  <p key={face.id} className="hk-line">
                    <b><bdi>{face.name}</bdi></b>
                    <span className="hk-pick">
                      {state.rooms.map((room, i) => (
                        <button key={room.id} type="button" onClick={() => void meeting.sendTo(face.id, room.id)}>
                          {i + 1}
                        </button>
                      ))}
                    </span>
                  </p>
                ))}
              </div>
            ) : null}
            <button
              className="btn btn-p btn-w"
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void meeting.closeSmallRooms().then(() => {
                  setBusy(false);
                  onClose();
                });
              }}
            >
              Bring everyone back
            </button>
          </>
        ) : (
          <>
            <div className="sx-sec">
              <p className="sx-l">How many rooms</p>
              <div className="hk-rules">
                {ROOM_CHOICES.map((n) => (
                  <button key={n} type="button" className={n === count ? "on" : undefined} onClick={() => setCount(n)}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="sx-sec">
              <p className="sx-l">Who goes where</p>
              <div className="hk-rules">
                <button type="button" className={spread === "auto" ? "on" : undefined} onClick={() => setSpread("auto")}>
                  Spread everyone out
                </button>
                <button type="button" className={spread === "by-hand" ? "on" : undefined} onClick={() => setSpread("by-hand")}>
                  I will put people in
                </button>
              </div>
            </div>
            <button
              className="btn btn-p btn-w"
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void meeting.openSmallRooms(count, spread).then(() => setBusy(false));
              }}
            >
              {busy ? "Opening" : "Start"}
            </button>
            <p className="hk-note">Everyone keeps the chat while they are in a small room.</p>
          </>
        )}
      </div>
    </Sheet>
  );
}

// --- the questions the room asked ---

export function HostQuestions({ chat, pin }: { chat: RoomChat; pin: (id: string | null) => void }): ReactNode {
  const qs = useQuestions(chat.lines, chat.pinned?.id ?? null, pin);
  const q = qs.queue.current;
  return (
    <div className="pp">
      <p className="pp-t">
        Questions{" "}
        <span className="muted" style={{ fontSize: "var(--t-small)" }}>
          {qs.queue.current ? `${qs.queue.place} of ${qs.queue.all.length}` : ""}
        </span>
      </p>
      {q ? (
        <div className="hk-q">
          <p className="hk-qn">
            <b><bdi>{q.name}</bdi></b>
            {qs.answering ? <em>Answering live</em> : null}
          </p>
          <p className="hk-qt" dir="auto">{q.body}</p>
          <div className="hk-qa">
            {qs.answering ? (
              <button type="button" className="btn btn-p btn-s" onClick={qs.finish}>
                Done
              </button>
            ) : (
              <>
                <button type="button" className="btn btn-p btn-s" onClick={qs.answerLive}>
                  Answer live
                </button>
                <button type="button" className="btn btn-q" onClick={qs.skip}>
                  Skip
                </button>
                {qs.queue.open.length > 1 ? (
                  <button type="button" className="btn btn-q" onClick={qs.next}>
                    Next
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : (
        <p className="pp-more">{qs.queue.all.length ? "No questions waiting. You are all caught up." : "Questions show here as people ask in the chat."}</p>
      )}
      {qs.queue.skipped.length ? (
        <button type="button" className="btn btn-q btn-s" style={{ margin: "8px" }} onClick={qs.bringBack}>
          Skipped ({qs.queue.skipped.length})
        </button>
      ) : null}
      {qs.queue.upNext.map((u) => (
        <button key={u.id} type="button" className="pp-r hk-up" onClick={() => qs.pick(u.id)}>
          <span className="pp-w">
            <b><bdi>{u.name}</bdi></b>
            <span dir="auto">{u.body}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

// --- the page the host leaves from ---

/**
 * A yes or no that opens as a small card at the button that asked, in the dock menus' own look. A phone keeps the
 * bottom sheet it always had. Escape, a click outside and the quiet button all close it.
 */
function Confirm({ label, anchor, above, onClose, children }: { label: string; anchor: string; above: boolean; onClose: () => void; children: ReactNode }): ReactNode {
  const phone = useMediaQuery(PHONE_QUERY);
  const card = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = card.current;
    if (!el) return;
    const at = document.querySelector(anchor)?.getBoundingClientRect();
    const w = el.offsetWidth;
    const room = document.documentElement.clientWidth;
    if (at && at.width > 0) {
      // Right edge to the button's right edge when the card opens upward, left to left when it opens down.
      const left = above ? at.right - w : at.left;
      el.style.left = `${Math.max(8, Math.min(room - w - 8, left))}px`;
      el.style.top = `${Math.max(8, above ? at.top - el.offsetHeight - 10 : at.bottom + 10)}px`;
    } else {
      el.style.left = `${Math.max(8, (room - w) / 2)}px`;
      el.style.top = "80px";
    }
  });
  useEffect(() => {
    if (phone) return;
    const away = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (t.isConnected && !card.current?.contains(t) && !t.closest(anchor)) onClose();
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [phone, anchor, onClose]);
  if (phone) {
    return (
      <Sheet label={label} onClose={onClose}>
        <div className="sx-in">{children}</div>
      </Sheet>
    );
  }
  return (
    <div ref={card} className="dmenu confirm" role="dialog" aria-label={label}>
      {children}
    </div>
  );
}

/** What leaving mid-live does, in the host's words. */
export function LeaveSheet({ onEnd, onKeep, onStay }: { onEnd: () => void; onKeep: () => void; onStay: () => void }): ReactNode {
  return (
    <Confirm label="Leave the studio" anchor=".lv-back" above={false} onClose={onStay}>
      <h3 className="sx-h">End the live, or keep it on and come back?</h3>
      <p className="hk-note">If you keep it on and leave, the live stays on for 5 minutes. It ends by itself if you are not back by then.</p>
      <p className="hk-note">If you close this tab, the live ends after 1 minute.</p>
      <div className="hk-leave">
        <button className="btn btn-p btn-w hk-end" type="button" onClick={onEnd}>
          End the live
        </button>
        <button className="btn btn-g btn-w" type="button" onClick={onKeep}>
          Keep it on and leave
        </button>
        <button className="btn btn-q btn-w" type="button" autoFocus onClick={onStay}>
          Stay in the room
        </button>
      </div>
    </Confirm>
  );
}

export function EndSheet({ busy, error, onKeep, onGo }: { busy: boolean; error: string; onKeep: () => void; onGo: () => void }): ReactNode {
  return (
    <Confirm label="End the live" anchor=".lv-dock .db.leave" above onClose={onKeep}>
      <h3 className="sx-h">End the live for everyone?</h3>
      <p className="hk-note">The replay starts building the moment you end.</p>
      {error ? <p className="hk-note bad">{error}</p> : null}
      <div className="hk-leave pair">
        <button className="btn btn-p hk-end" type="button" disabled={busy} onClick={onGo}>
          {busy ? "Ending" : "End the live"}
        </button>
        <button className="btn btn-g" type="button" autoFocus disabled={busy} onClick={onKeep}>
          Keep going
        </button>
      </div>
    </Confirm>
  );
}

/** How often the ended screen asks again while the replay is still being built. */
const REPLAY_LOOK_MS = 10_000;

/** Where "Talk with the room" goes: the room's Everyone, or its Announcements when Everyone chat is off. */
function useTalkTo(info: LiveStudio): string {
  const [talkTo, setTalkTo] = useState(everyonePath(info.webinarId));
  useEffect(() => {
    if (info.kind !== "room") return;
    let alive = true;
    api.talk.room(info.webinarId).then((r) => alive && setTalkTo(roomHome(r)), () => undefined);
    return () => {
      alive = false;
    };
  }, [info.webinarId, info.kind]);
  return talkTo;
}

/** After the live: the session's own numbers, and where this kind of live takes the host next. */
export function HostEnded({ info, next, report, go }: { info: LiveStudio; next: string | null; report: string; go: (to: string) => void }): ReactNode {
  const [summary, setSummary] = useState<WebinarLive | null>(null);
  const { kind, circleId } = info;
  const talkTo = useTalkTo(info);
  // Why read again: who came is counted from the live, so what the studio opened with is out of date once it ends. A room's
  // numbers come from its session report instead, since its next session may be the one the studio would answer with.
  const [after, setAfter] = useState(info);
  useEffect(() => {
    if (info.kind === "room") return;
    let alive = true;
    api.studio.get(info.webinarId).then((s) => alive && setAfter(s), () => undefined);
    return () => {
      alive = false;
    };
  }, [info.webinarId, info.kind]);
  // Why asked again: the replay is built after the live ends, so the line moves from building to ready without a reload.
  useEffect(() => {
    let alive = true;
    let timer = 0;
    const look = () => {
      api.board
        .session(info.webinarId, info.sessionId)
        .then((s) => {
          if (!alive) return;
          setSummary(s);
          if (!s.replay.playable && s.replay.videoState !== "error") timer = window.setTimeout(look, REPLAY_LOOK_MS);
        })
        .catch(() => {
          if (alive) timer = window.setTimeout(look, REPLAY_LOOK_MS);
        });
    };
    look();
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [info.webinarId, info.sessionId]);
  const answered = summary?.questions.filter((q) => q.answered === "live").length ?? 0;
  const { names } = after.came;
  const came = kind === "room" ? (summary?.cameLive ?? 0) : after.came.count;
  const members = kind === "room" ? (summary?.registered ?? 0) : after.registered;
  const replay = replayGoes(kind, replayLine(summary, true), came, members);
  const n = (v: number) => v.toLocaleString("en-US");
  const line =
    kind === "quick"
      ? cameLine(came, names.map(firstOf))
      : kind === "circle"
        ? `${n(came)} of the ${n(members)} came.`
        : summary
          ? `${n(came)} came of ${n(members)} registered${summary.questions.length ? `, ${answered} questions answered` : ""}${summary.report?.offer ? `, ${n(summary.report.offer.clicked)} clicked the offer` : ""}.`
          : null;
  const to = (path: string, label: string, primary = false) => (
    <button className={primary ? "btn btn-p btn-m" : "btn btn-g btn-m"} type="button" onClick={() => go(path)}>
      {label}
    </button>
  );
  return (
    <div className="lv-end hk-ended">
      <div className="lv-end-c">
        <p className="eyebrow" dir="auto">{info.title}</p>
        <h2>{kind === "circle" ? "The call has ended." : "The live has ended."}</h2>
        {line ? <p>{line}</p> : null}
        {kind === "room" && summary && summary.teamTests > 0 ? <p className="hk-note">{teamTestsLine(summary.teamTests)}</p> : null}
        {kind === "room" && next ? <p className="hk-note">{next}</p> : null}
        {replay ? <p className="hk-note">{replay}</p> : null}
        {kind === "room" ? (
          <span className="hk-leave">
            {to(report, "See how it went", true)}
            {to(talkTo, "Talk with the room")}
          </span>
        ) : kind === "quick" ? (
          <span className="hk-leave">
            {to(`/rooms/${info.webinarId}/call`, "See how it went", true)}
            {to("/rooms", "Back to Rooms")}
          </span>
        ) : kind === "circle" && circleId ? (
          <span className="hk-leave">{to(circlePath(circleId), "Back to the circle", true)}</span>
        ) : null}
      </div>
    </div>
  );
}

/** The ask count the Hands tab shows, and whether it is new since the host last looked there. */
export function useHandsLit(waiting: number, looking: boolean): boolean {
  const seen = useRef(waiting);
  const [lit, setLit] = useState(false);
  useEffect(() => {
    if (looking) {
      seen.current = waiting;
      setLit(false);
    } else if (waiting > seen.current) setLit(true);
    else if (waiting < seen.current) seen.current = waiting;
  }, [waiting, looking]);
  return lit;
}

// --- the planned moments ---

const KIND_NAME: Record<InteractionType, string> = {
  poll: "Poll",
  question: "Quick question",
  reaction: "Reaction",
  handout: "Handout",
};

const ACTION_LABEL: Record<MomentAction, string> = { start: "Start now", skip: "Skip", close: "Close" };

const DONE_WORDS: Partial<Record<MomentStanding, string>> = { shown: "Shown", skipped: "Skipped", closed: "Closed" };

/** The line under a moment: what it is, and when it lands or how it ended. */
function momentLine(type: InteractionType, standing: MomentStanding, appearsAt: number): string {
  if (standing === "planned") return `${KIND_NAME[type]} · ${aboutMinutes(appearsAt)}`;
  if (standing === "up") return KIND_NAME[type];
  return `${KIND_NAME[type]} · ${DONE_WORDS[standing]}`;
}


function aboutMinutes(sec: number): string {
  const min = Math.round(sec / 60);
  return min === 1 ? "about 1 minute in" : `about ${min} minutes in`;
}



/** The board's planned moments: what is up now, what is still to come, and what is done, in the room's look. */
export function HostMoments({
  moments,
  runs,
  into,
  wentLiveAt,
  busy,
  onAct,
}: {
  moments: readonly LiveStudioMoment[];
  runs: readonly MomentRun[];
  into: number;
  wentLiveAt: number | null;
  busy: string | null;
  onAct: (id: string, action: MomentAction) => void;
}): ReactNode {
  const rows = moments.map((m) => ({ m, standing: wentLiveAt === null ? ("planned" as const) : standingOf(m, moments, runs, into, wentLiveAt) }));
  const groups: [string, typeof rows][] = [
    ["Up now", rows.filter((r) => r.standing === "up")],
    ["Still to come", rows.filter((r) => r.standing === "planned")],
    ["Done", rows.filter((r) => r.standing !== "up" && r.standing !== "planned")],
  ];
  return (
    <div className="pp">
      <p className="pp-t">Moments</p>
      {groups.map(([title, list]) =>
        list.length ? (
          <div key={title}>
            <p className="pp-g">{title}</p>
            {list.map(({ m, standing }) => (
              <div key={m.id} className="pp-r hk-row">
                <div className="pp-w">
                  <b>{m.words || momentLine(m.type, standing, m.appears_at)}</b>
                  <span>{momentLine(m.type, standing, m.appears_at)}</span>
                </div>
                <span className="hk-rowact">
                  {(wentLiveAt === null ? [] : actionsFor(standing)).map((a) => (
                    <button key={a} type="button" className={a === "skip" ? "hk-no sm" : "hk-go sm"} disabled={busy === m.id} onClick={() => onAct(m.id, a)}>
                      {ACTION_LABEL[a]}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>
        ) : null,
      )}
    </div>
  );
}
